/**
 * EvidenceRefsBlock — renders an `evidence_refs` content block using the
 * existing EvidenceRefList component.
 *
 * When a valid `patientId` is provided, refs are interactive: clicking a ref
 * opens the source-record detail modal via `useReferenceModal`. When
 * `patientId` is null/0, refs render as non-interactive plain text (no context
 * provider, no onRefClick).
 *
 * Mapping: XoeEvidenceRefRow → EvidenceRef
 *   source_type → source (cast as EvidenceSource; EvidenceRefList gracefully
 *                 handles unknown sources via SOURCE_LABEL fallback)
 *   label       → source-specific field based on source_type so formatRef() in
 *                 EvidenceRefList picks it up correctly:
 *                   blood_results / lab_results → test_name
 *                   genetic_panel              → gene
 *                   medical_history            → condition
 *                   medications                → medication
 *                   diet_scores / exercise_scores / questionnaire → field
 *                   (all others)               → note
 *   record_id   → stringified (EvidenceRef.record_id is string | undefined)
 *   record_date → passed through
 */

import React from 'react';

import { EvidenceRefList } from '../../healthcare/evidenceRefs/EvidenceRefList';
import { EvidenceRefInteractionContext } from '../../healthcare/evidenceRefs/EvidenceRefInteractionContext';
import { useReferenceModal } from '../../../hooks/xoe-actions/useReferenceModal';
import type { EvidenceRef, EvidenceSource } from '../../../types/healthcare/PatientAnalysisTypes';
import type { XoeEvidenceRefRow } from '../../../types/xoe/XoeTypes';
import type { InteractiveProps, EvidenceRefsBlockProps } from '../../../types/xoe/XoeUiTypes';

// ── Ref mapping ───────────────────────────────────────────────────────────────

// source_type is free text the model picks ("literature", "medical_literature", …), so a PMID in the label counts too.
function isLiteratureRef(row: XoeEvidenceRefRow): boolean {
	const source = (row.source_type ?? '').toLowerCase();
	return source.includes('literature') || source === 'pubmed' || /\bPMID\b/i.test(row.label ?? '');
}

function mapRow(row: XoeEvidenceRefRow): EvidenceRef {
	const source = row.source_type as EvidenceSource;
	const base: EvidenceRef = {
		source,
		...(row.record_id !== undefined ? { record_id: String(row.record_id) } : {}),
		...(row.record_date ? { record_date: row.record_date } : {}),
	};

	// Route the label string to the field that formatRef() reads for this source type.
	switch (source) {
		case 'blood_results':
		case 'lab_results':
			return { ...base, test_name: row.label };
		case 'genetic_panel':
			return { ...base, gene: row.label };
		case 'medical_history':
			return { ...base, condition: row.label };
		case 'medications':
			return { ...base, medication: row.label };
		case 'diet_scores':
		case 'exercise_scores':
		case 'questionnaire':
			return { ...base, field: row.label };
		default:
			// Unknown source — fall back to note so the label still surfaces in the UI.
			return { ...base, note: row.label };
	}
}

// ── Interactive (patientId is a valid positive number) ────────────────────────

const EvidenceRefsInteractive: React.FC<InteractiveProps> = ({ refs, patientId }) => {
	const { openRef, modals } = useReferenceModal(patientId);
	return (
		<EvidenceRefInteractionContext.Provider value={{ onRefClick: openRef }}>
			<EvidenceRefList refs={refs} />
			{modals}
		</EvidenceRefInteractionContext.Provider>
	);
};

// ── Public block component ────────────────────────────────────────────────────

const EvidenceRefsBlock: React.FC<EvidenceRefsBlockProps> = ({ refs, patientId }) => {
	// `refs` may be undefined on a malformed backend block — guard so the child
	// render can't throw and crash the whole message thread.
	// Literature rows are the model's unlinked copy of the server's `literature` block, which renders them with links.
	const mapped: EvidenceRef[] = (refs ?? []).filter((row) => !isLiteratureRef(row)).map(mapRow);

	if (mapped.length === 0) return null;

	if (patientId) {
		return <EvidenceRefsInteractive refs={mapped} patientId={patientId} />;
	}

	// No patient context — render read-only (no provider, no onRefClick).
	return <EvidenceRefList refs={mapped} />;
};

export default EvidenceRefsBlock;
